import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './UserBookingInfo.css';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment-timezone';
import { fetchUserData } from '../../../../Utilities/api';
import { BackButton } from '../../../../Utilities/components/Buttons';

const UserBookingInfo = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [user, setUser] = useState([]);
  const [date, setDate] = useState(new Date());
  const [activeDate, setActiveDate] = useState(null);
  const [selectedDateBookings, setSelectedDateBookings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const userData = await fetchUserData(userId);
        if (userData && userData.Bookings) {
          setUser(userData);
          setBookings(userData.Bookings);
        } else {
          setBookings([]);
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch user data');
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [userId]);

  const formatDate = (inputDate) => {
    return moment(inputDate).format('MM/DD/YY');
  }

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phoneNumber; // Return original if format is invalid
  };

  const handleDateChange = (selectedDate) => {
    const localDate = moment(selectedDate).tz(moment.tz.guess()).startOf('day').toDate();
    setDate(localDate);
    setActiveDate(localDate.toISOString().split('T')[0]);
    const formattedDate = localDate.toISOString().split('T')[0];

    const dayBookings = (bookings || []).filter(booking =>
      booking &&
      booking.status !== 'Canceled' &&
      moment(booking.startTime).tz(moment.tz.guess()).format('YYYY-MM-DD') === formattedDate
    );
    setSelectedDateBookings(dayBookings);
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const localDate = moment(date).tz(moment.tz.guess()).startOf('day').toDate();
      const formattedDate = localDate.toISOString().split('T')[0];
      const today = moment().tz(moment.tz.guess()).startOf('day').toISOString().split('T')[0];
      const isActive = formattedDate === activeDate;

      const dayBookings = (bookings || []).filter(booking =>
        booking &&
        booking.status !== 'Canceled' &&
        moment(booking.startTime).tz(moment.tz.guess()).format('YYYY-MM-DD') === formattedDate
      );

      let tileClassName = "";
      if (formattedDate < today && dayBookings.length > 0) {
        tileClassName = isActive ? "history-tile-active" : "history-tile";
      } else if (formattedDate === today) {
        tileClassName = isActive ? "today-tile-active" : "today-tile";
      } else if (dayBookings.length > 0) {
        tileClassName = isActive ? "booked-tile-active" : "booked-tile";
      }

      if (tileClassName && !isActive) {
        return (
          <div className={tileClassName}>
            <abbr className='reserved-abbr' aria-label={formattedDate}>{localDate.getDate()}</abbr>
          </div>
        );
      } else {
        return (
          <div className={tileClassName}></div>
        );
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <p className="loading-text">Loading bookings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container error">
        <p>Error: {error}</p>
        <button onClick={() => navigate(`/console/users/${userId}`)}>
          Back to User
        </button>
      </div>
    );
  }

  return (
    <>
          <BackButton />
        <div className="user-booking-user-info-container">
            {user.image && (
            <div className="user-booking-pet-image-container">
                <img src={user.image} alt={`Image of ${user.name}`} className="user-booking-user-image" />
            </div>
            )}
            <div className="user-booking-user-info-text">
            <h2 className="user-booking-user-info-title">{user.username}</h2>
            <div className="user-booking-user-details-grid">
                <div>
                <p><span className="user-booking-user-detail-label">Email:</span> {user.email}</p>
                <p><span className="user-booking-user-detail-label">Phone:</span> {formatPhoneNumber(user.phone) || 'N/A'}</p>
                <p><span className="user-booking-user-detail-label">Store Location:</span> {user.storeLocation || 'N/A'}</p>
                </div>
            </div>
            </div>
        </div>
    
        <div className="events-container">
            <div className="legend">
                <div className="legend-item">
                <span className="history-dot" />
                History
                </div>
                <div className="legend-item">
                <span className="today-dot" />
                Today
                </div>
                <div className="legend-item">
                <span className="booked-dot" />
                Booked
                </div>
                <div className="legend-item">
                <span className="events-dot" />
                Events
                </div>
            </div>

            <div className="calendar-container">
                <Calendar
                calendarType="gregory"
                onChange={handleDateChange}
                value={date}
                tileContent={tileContent}
                />
            </div>

            <div className="event-details">
                <h3 className='event-details-header'>Day Overview</h3>
                <hr className='booking-hr' />
                {selectedDateBookings.length > 0 ? (
                <ul>
                    {selectedDateBookings.map((transaction, index) => {
                    const targetPath = `/console/userPaymentDetails/${userId}/${transaction.id}`;
                    return (
                        <li key={index} className='event-details-item'>
                        <div className="date">{formatDate(transaction.startTime)}</div>
                        <div className="title">{transaction.bookingType}</div>
                        <div className="description">
                            {transaction.selectedPets.map((pet) => (
                            <p key={pet.petName} className='event-details-pet-name'>({pet.petName})</p>
                            ))}
                        </div>
                        <button onClick={() => navigate(targetPath, { state: { transaction } })}>View</button>
                        </li>
                    );
                    })}
                </ul>
                ) : (
                <p className='event-details-message'>No visits or events for the selected date</p>
                )}
            </div>
        </div>
    </>
  );
};

export default UserBookingInfo;